import type Phaser from 'phaser';
import type { BiomeId } from '../world/biome';
import { TerrainType } from '../world/terrain';

/**
 * Painted art (D133): ground textures and prop sprites generated for each
 * biome (Magnific / Nano Banana, our own prompts), processed into
 * `public/art/`. Everything here is optional — a biome without painted art
 * falls back to the procedural art, so the game never depends on it.
 */
export type WaterKind = 'clear' | 'toxic' | 'deep';
export type RoadKind = 'asphalt' | 'dirt';
export type CliffKind = 'grey' | 'red';

/** Prop roles, in the order the props sheets were prompted (3×3, reading order). */
export const PROP_ROLES = ['tallTree', 'roundTree', 'smallTree', 'deadTree', 'bush', 'bushes', 'boulder', 'rocks', 'tuft'] as const;
export type PropRole = (typeof PROP_ROLES)[number];

export interface BiomeAssets {
  readonly water: WaterKind;
  readonly road: RoadKind;
  readonly cliff: CliffKind;
  /** Prop sprites per role: props/<biome>/<role>-<n>.webp. */
  readonly props: Readonly<Partial<Record<PropRole, number>>>;
}

const P = (water: WaterKind, road: RoadKind, cliff: CliffKind, props: Partial<Record<PropRole, number>>): BiomeAssets => ({ water, road, cliff, props });
const ALL1 = { tallTree: 1, roundTree: 1, smallTree: 1, deadTree: 1, bush: 1, bushes: 1, boulder: 1, rocks: 1, tuft: 1 } as const;

/** Biomes with painted art (from tools/process-art.py). */
export const PAINTED: Partial<Record<BiomeId, BiomeAssets>> = {
  valley: P('clear', 'asphalt', 'grey', ALL1),
  prairie: P('clear', 'dirt', 'grey', { roundTree: 1, tallTree: 1, bushes: 1, deadTree: 1, bush: 1, rocks: 1, boulder: 1, tuft: 1 }),
  desert: P('clear', 'dirt', 'red', { tallTree: 1, smallTree: 1, bush: 1, roundTree: 1, boulder: 1, rocks: 2, deadTree: 1, bushes: 1 }),
  swamp: P('toxic', 'asphalt', 'grey', { roundTree: 1, tallTree: 1, tuft: 1, bush: 1, boulder: 1, rocks: 1, bushes: 1, deadTree: 1 }),
  tundra: P('deep', 'asphalt', 'grey', ALL1),
  autumn: P('clear', 'asphalt', 'grey', ALL1),
  canyon: P('clear', 'dirt', 'red', { boulder: 2, rocks: 2, bush: 1, roundTree: 1, deadTree: 1, bushes: 1 }),
  deadwood: P('toxic', 'asphalt', 'grey', { tallTree: 1, deadTree: 2, roundTree: 1, boulder: 1, rocks: 1, bush: 1, bushes: 1, tuft: 1 }),
};

export const SHARED_PROPS = { ruins: 9, infected: 9 };

/** A role a biome lacks borrows the nearest one it has. */
const FALLBACK: Record<PropRole, readonly PropRole[]> = {
  tallTree: ['roundTree', 'smallTree'],
  roundTree: ['tallTree', 'smallTree'],
  smallTree: ['tallTree', 'roundTree', 'bush'],
  deadTree: ['tallTree'],
  bush: ['bushes', 'tuft'],
  bushes: ['bush', 'tuft'],
  boulder: ['rocks'],
  rocks: ['boulder'],
  tuft: ['bush', 'bushes'],
};

/** Sprite file stem for a role in a biome (with fallback), variant chosen by `r` in [0, 1). */
export function propFor(b: BiomeId, role: PropRole, r: number): { role: PropRole; key: string } | null {
  const a = PAINTED[b];
  if (!a) return null;
  for (const want of [role, ...FALLBACK[role]]) {
    const n = a.props[want] ?? 0;
    if (n > 0) return { role: want, key: propKey(b, `${want}-${Math.floor(r * n)}`) };
  }
  return null;
}

const base = (): string => (import.meta.env?.BASE_URL ?? '/') + 'art/';

export const groundKey = (b: BiomeId, which: 'ground' | 'alt'): string => `paint-${b}-${which}`;
export const sharedKey = (name: string): string => `paint-${name}`;
export const propKey = (set: string, i: number | string): string => `paint-prop-${set}-${i}`;

/** Queue the painted images a biome needs (call from a scene's preload). */
export function preloadPainted(scene: Phaser.Scene, biome: BiomeId | undefined): void {
  const b = biome ?? 'valley';
  const a = PAINTED[b];
  if (!a) return;
  const root = base();
  scene.load.image(groundKey(b, 'ground'), `${root}${b}/ground.webp`);
  scene.load.image(groundKey(b, 'alt'), `${root}${b}/alt.webp`);
  scene.load.image(sharedKey(`water-${a.water}`), `${root}shared/water-${a.water}.webp`);
  scene.load.image(sharedKey(`road-${a.road}`), `${root}shared/road-${a.road}.webp`);
  if (a.road !== 'asphalt') scene.load.image(sharedKey('road-asphalt'), `${root}shared/road-asphalt.webp`); // ruins
  scene.load.image(sharedKey(`cliff-${a.cliff}`), `${root}shared/cliff-${a.cliff}.webp`);
  for (const [role, n] of Object.entries(a.props)) for (let i = 0; i < (n ?? 0); i++) scene.load.image(propKey(b, `${role}-${i}`), `${root}${b}/props/${role}-${i}.webp`);
  for (let i = 0; i < SHARED_PROPS.ruins; i++) scene.load.image(propKey('ruins', i), `${root}shared/ruins/${i}.webp`);
  for (let i = 0; i < SHARED_PROPS.infected; i++) scene.load.image(propKey('infected', i), `${root}shared/infected/${i}.webp`);
}

/** Which painted texture (and how much to darken it) paints each terrain type. */
export function terrainTexture(b: BiomeId, t: TerrainType): { key: string; shade: number } | null {
  const a = PAINTED[b];
  if (!a) return null;
  switch (t) {
    case TerrainType.Grass:
      return { key: groundKey(b, 'ground'), shade: 0 };
    case TerrainType.Meadow:
      return { key: groundKey(b, 'alt'), shade: 0 };
    case TerrainType.Dirt:
      return { key: groundKey(b, 'alt'), shade: 0.18 };
    case TerrainType.Forest:
      return { key: groundKey(b, 'ground'), shade: 0.32 };
    case TerrainType.Rock:
      return { key: sharedKey(`cliff-${a.cliff}`), shade: 0 };
    case TerrainType.Water:
      return { key: sharedKey(`water-${a.water}`), shade: 0 };
    case TerrainType.Road:
      return { key: sharedKey(`road-${a.road}`), shade: 0 };
    case TerrainType.Ruins:
      return { key: sharedKey('road-asphalt'), shade: 0.25 };
    default:
      return null;
  }
}

/** Display width (world px) per prop role — trees read about two tiles tall. */
export const PROP_WIDTH: Record<PropRole, number> = {
  tallTree: 62,
  roundTree: 74,
  smallTree: 46,
  deadTree: 56,
  bush: 30,
  bushes: 42,
  boulder: 50,
  rocks: 38,
  tuft: 26,
};

/** Building types with painted art (public/art/buildings/<type>.webp). */
export const PAINTED_BUILDINGS: readonly string[] = ['command_center', 'wall', 'sawmill', 'warehouse', 'farm', 'scrap_yard', 'survivor_housing', 'workshop', 'watchtower', 'gate', 'gun_tower', 'barracks', 'generator', 'horde_beacon', 'flame_tower', 'survivor_camp', 'research_lab', 'infected_nest'];

export function preloadBuildings(scene: Phaser.Scene): void {
  for (const t of PAINTED_BUILDINGS) scene.load.image(`paint-bld-${t}`, `${base()}buildings/${t}.webp`);
}

/** Unit types with a painted game sprite (public/art/units/<type>.webp). */
export const PAINTED_UNITS: readonly string[] = ['walker', 'runner', 'brute', 'bloater', 'screamer', 'ranger', 'heavy', 'engineer', 'sniper', 'flamer'];

export function preloadUnits(scene: Phaser.Scene): void {
  for (const t of PAINTED_UNITS) scene.load.image(`paint-unit-${t}`, `${base()}units/${t}.webp`);
}
